import { useCallback, useEffect } from 'react';
import Promise from 'bluebird';

import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { FolderAssetType, AdsStudioPageName, getSearchQuery, AdditionalQueries } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useLocalNavigation } from '@bingads-webui-campaign-react/ads-studio-shared-components';

// this hook is used to navigate between different pages in the app
export const useNavigation = ({
  rootRoute,
  history,
  search,
  forceToReload,
  adFilterStore,
  updateWaterfallFilter,
}) => {
  const localNavigation = useLocalNavigation();

  const gotoCampaignUIPage = useCallback(({ subpath }, openInNewTab) => {
    const url = `/campaign/vnext/${subpath}${search}`;
    if (openInNewTab && window && window.open) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  }, [history, search]);

  // home page
  const gotoHomePage = useCallback(({ redirect = true, reload = false, removeSearchQuery = [] } = {}) => {
    if (reload) {
      forceToReload();
    }
    const currentSearch = getSearchQuery({ search, removeSearchQuery });
    if (redirect) {
      history.replace(`${rootRoute}/home${currentSearch}`);
    } else {
      history.push(`${rootRoute}/home${currentSearch}`);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoSearchResultPage = useCallback(({ redirect = false, reload = false, query = '' } = {}) => {
    if (reload) {
      forceToReload();
    }
    const additionalQueries = {
      [AdditionalQueries.Query]: query,
    };
    const currentSearch = getSearchQuery({ search, additionalQueries });
    if (redirect) {
      history.replace(`${rootRoute}/home/search${currentSearch}`);
    } else {
      history.push(`${rootRoute}/home/search${currentSearch}`);
    }
  }, [forceToReload, history, rootRoute, search]);

  // performance page
  const gotoPerformancePage = useCallback(({ redirect = false, reload = false } = {}) => {
    if (reload) {
      forceToReload();
    }
    const currentSearch = getSearchQuery({ search });
    if (redirect) {
      history.replace(`${rootRoute}/performance${currentSearch}`);
    } else {
      history.push(`${rootRoute}/performance${currentSearch}`);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoImagesPage = useCallback(({ redirect = false, reload = false, removeSearchQuery = [] } = {}) => {
    if (reload) {
      forceToReload();
    }
    const currentSearch = getSearchQuery({ search, removeSearchQuery });
    if (redirect) {
      history.replace(`${rootRoute}/images${currentSearch}`);
    } else {
      history.push(`${rootRoute}/images${currentSearch}`);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoVideosPage = useCallback(({ redirect = false, reload = false } = {}) => {
    if (reload) {
      forceToReload();
    }
    const currentSearch = getSearchQuery({ search });
    if (redirect) {
      history.replace(`${rootRoute}/videos${currentSearch}`);
    } else {
      history.push(`${rootRoute}/videos${currentSearch}`);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoLibraryPage = useCallback(({ redirect = false, reload = false } = {}) => {
    if (reload) {
      forceToReload();
    }
    const currentSearch = getSearchQuery({ search });
    if (redirect) {
      history.replace(`${rootRoute}/library${currentSearch}`);
    } else {
      history.push(`${rootRoute}/library${currentSearch}`);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoEditImagePage = useCallback(({
    imageId, folderId, editImageUrl, redirect = false, reload = false, initialCurrentView = '', query,
  } = {}) => {
    if (reload) {
      forceToReload();
    }
    const additionalQueries = {
      [AdditionalQueries.FolderId]: folderId,
      [AdditionalQueries.EditImageUrl]: editImageUrl,
    };
    if (initialCurrentView) {
      additionalQueries[AdditionalQueries.InitialCurrentView] = initialCurrentView;
    }
    if (query) {
      additionalQueries[AdditionalQueries.Query] = query;
    }
    const currentSearch = getSearchQuery({ search, additionalQueries });
    const url = `${rootRoute}/images/${imageId}/edit${currentSearch}`;
    if (redirect) {
      history.replace(url);
    } else {
      history.push(url);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoEditDisplayImagePage = useCallback(({
    imageId, folderId, templateId, redirect = true, reload = false, query,
  } = {}) => {
    if (reload) {
      forceToReload();
    }

    const additionalQueries = {
      [AdditionalQueries.FolderId]: folderId,
      [AdditionalQueries.TemplateId]: templateId,
    };
    if (query) {
      additionalQueries[AdditionalQueries.Query] = query;
    }
    const currentSearch = getSearchQuery({ search, additionalQueries });
    const url = `${rootRoute}/displaytemplates/${imageId}/edit${currentSearch}`;
    if (redirect) {
      history.replace(url);
    } else {
      history.push(url);
    }
  }, [forceToReload, history, rootRoute, search]);

  // goback
  const goBack = useCallback(({ reload } = {}) => {
    if (reload) {
      forceToReload();
    }
    // pop up local views first
    if (localNavigation.getTopView()) {
      localNavigation.popLocalView();
      return;
    }
    history.goBack();
  }, [forceToReload, history, localNavigation]);

  const gotoWebsiteImagePanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.WebsiteImagePanelPage, params);
  }, [localNavigation]);

  const gotoStockImagePanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.StockImagePanelPage, params);
  }, [localNavigation]);

  const gotoInvestImagePanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.InvestImagePanelPage, params);
  }, [localNavigation]);

  const gotoInvestVideoPanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.InvestVideoPanelPage, params);
  }, [localNavigation]);

  const gotoAIGenerationPanelPage = useCallback(({ folderId, redirect = false, reload = false } = {}) => {
    if (reload) {
      forceToReload();
    }
    const additionalQueries = {
      [AdditionalQueries.FolderId]: folderId,
    };
    const currentSearch = getSearchQuery({ search, additionalQueries });
    if (redirect) {
      history.replace(`${rootRoute}/generateimage${currentSearch}`);
    } else {
      history.push(`${rootRoute}/generateimage${currentSearch}`);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoDisplayTemplatesCreationPage = useCallback(({ redirect, templateGroupId } = {}) => {
    const additionalQueries = {
      [AdditionalQueries.TemplateGroupId]: templateGroupId,
    };
    const currentSearch = getSearchQuery({ search, additionalQueries });

    if (redirect) {
      history.replace(`${rootRoute}/displaytemplates/create${currentSearch}`);
    } else {
      history.push(`${rootRoute}/displaytemplates/create${currentSearch}`);
    }
  }, [history, rootRoute, search]);

  const gotoDisplayTemplatesPage = useCallback(({ redirect, removeSearchQuery = [] } = {}) => {
    const currentSearch = getSearchQuery({ search, removeSearchQuery });

    if (redirect) {
      history.replace(`${rootRoute}/displaytemplates${currentSearch}`);
    } else {
      history.push(`${rootRoute}/displaytemplates${currentSearch}`);
    }
  }, [history, rootRoute, search]);

  const gotoVideoTemplatePage = useCallback(({ redirect = false, reload = false } = {}) => {
    if (reload) {
      forceToReload();
    }
    const currentSearch = getSearchQuery({ search });
    if (redirect) {
      history.replace(`${rootRoute}/videotemplates${currentSearch}`);
    } else {
      history.push(`${rootRoute}/videotemplates${currentSearch}`);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoCreateVideoPage = useCallback(({ redirect = false, reload = false } = {}) => {
    if (reload) {
      forceToReload();
    }
    const currentSearch = getSearchQuery({ search });
    const url = `${rootRoute}/video/create${currentSearch}`;
    if (redirect) {
      history.replace(url);
    } else {
      history.push(url);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoEditVideoPage = useCallback(({ videoId, redirect = false, reload = false } = {}) => {
    if (reload) {
      forceToReload();
    }
    const currentSearch = getSearchQuery({ search });
    const url = `${rootRoute}/video/${videoId}/edit${currentSearch}`;
    if (redirect) {
      history.replace(url);
    } else {
      history.push(url);
    }
  }, [forceToReload, history, rootRoute, search]);

  const gotoAddVideoPanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.AddVideoPanelPage, params);
  }, [localNavigation]);

  const gotoUploadFilesPanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.UploadFilesPanelPage, params);
  }, [localNavigation]);

  const gotoUploadFolderPanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.UploadFolderPanelPage, params);
  }, [localNavigation]);

  const gotoMixedImagePanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.MixedImagePanelPage, params);
  }, [localNavigation]);

  const gotoMixedVideoPanelPage = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.MixedVideoPanelPage, params);
  }, [localNavigation]);

  const gotoAssetDetails = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.AssetDetailsPage, params);
  }, [localNavigation]);

  const gotoBackgroundRemovalModal = useCallback((params) => {
    localNavigation.pushLocalView(AdsStudioPageName.BackgroundRemovalModalPage, params);
  }, [localNavigation]);

  const updateAssetDetailsParams = useCallback((params) => {
    const oldParams = localNavigation.getViewParams(AdsStudioPageName.AssetDetailsPage);
    const newParams = { ...oldParams, ...params };
    const oldParamsStr = JSON.stringify(oldParams);
    const newParamsStr = JSON.stringify(newParams);
    if (newParams && oldParamsStr !== newParamsStr) {
      localNavigation.setViewParams(AdsStudioPageName.AssetDetailsPage, newParams);
    }
  }, [localNavigation]);

  const gotoAdsPageWithFilter = useCallback(({ filterProperty, filterValue }, openInNewTab) => {
    // set ad grid filter
    const filter = {
      [filterProperty]: { $contains: filterValue },
    };
    const savingPromise = adFilterStore.save({ query: filter }, true);

    // set campaign type filter to All Campaigns
    updateWaterfallFilter({
      filterType: WaterfallFilterType.CampaignType,
      filter: { key: WaterfallFilterType.CampaignType, value: { query: null } },
    });

    // navigate to ads page once filter is saved
    const timeoutPromise = Promise.delay(3000);
    return Promise.any([savingPromise, timeoutPromise]).then(() => {
      gotoCampaignUIPage({ subpath: 'ads' }, openInNewTab);
    });
  }, [adFilterStore, gotoCampaignUIPage, updateWaterfallFilter]);

  const gotoAdsPageWithAssetIdFilter = useCallback(({ folderAssetType, assetId }, openInNewTab) => {
    const AssetIdFilterPropertyConfig = {
      [FolderAssetType.Image]: 'ResponsiveAdImageId',
      [FolderAssetType.Video]: 'ResponsiveAdVideoId',
    };
    const filterProperty = AssetIdFilterPropertyConfig[folderAssetType];
    const filterValue = assetId;
    return gotoAdsPageWithFilter({ filterProperty, filterValue }, openInNewTab);
  }, [gotoAdsPageWithFilter]);

  useEffect(() => {
    // pop up local views first when switch page
    if (localNavigation && localNavigation.getTopView && localNavigation.getTopView()) {
      localNavigation.popLocalView();
    }
  // localNavigation can not be included in the dependencies
  }, [history.location.pathname]);

  return {
    gotoHomePage,
    gotoPerformancePage,
    gotoImagesPage,
    gotoVideosPage,
    gotoLibraryPage,
    gotoEditImagePage,
    gotoEditDisplayImagePage,
    goBack,
    gotoCampaignUIPage,
    gotoAdsPageWithAssetIdFilter,
    gotoWebsiteImagePanelPage,
    gotoStockImagePanelPage,
    gotoInvestImagePanelPage,
    gotoInvestVideoPanelPage,
    gotoAIGenerationPanelPage,
    gotoDisplayTemplatesCreationPage,
    gotoDisplayTemplatesPage,
    gotoAddVideoPanelPage,
    gotoSearchResultPage,
    gotoUploadFilesPanelPage,
    gotoUploadFolderPanelPage,
    gotoMixedImagePanelPage,
    gotoMixedVideoPanelPage,
    gotoAssetDetails,
    updateAssetDetailsParams,
    gotoBackgroundRemovalModal,
    gotoVideoTemplatePage,
    gotoCreateVideoPage,
    gotoEditVideoPage,
  };
};
